import { Text, HStack, TextInput, VStack, Button, Square, Spinner, Box } from '@televet/kibble-ui';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import partial from 'lodash-es/partial';
import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetPaginatedAppointments } from 'shared/hooks/useGetPaginatedAppointments';
import { GetUserAppointmentGroupingsDocument, useUpdateOneUserAppointmentGroupingMutation } from 'shared/types/graphql';
import { AppointmentCard } from '../Card';
import { Appointment, AppointmentGrouping } from '../..';
import FilterAppointmentsMenu from '../Filters/FilterAppointmentsMenu';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { ModuleName } from '@televet/shared-types/productPackages';
import { useModularity } from 'shared/hooks/useModularity';

const appointmentsPerPage = 20; // tested on UF loading appointments in under 300ms
const scrollPixelVariance = 30; // certain situations it needs to be slightly here than the client height plus the scroll top
interface AppointmentGroupProps {
  currentDay: Date;
  userGroup: AppointmentGrouping;
  onAppointmentClicked: (appointment: Appointment) => void;
  onDeleteUserAppointmentGroup: (id: string) => void;
  channelStatusIds?: string[];
}

export function AppointmentGroup({
  currentDay,
  userGroup,
  onAppointmentClicked,
  onDeleteUserAppointmentGroup,
  channelStatusIds,
}: AppointmentGroupProps): JSX.Element {
  const [label, setLabel] = useState(userGroup.label);
  const [isEditLabelInputVisible, setIsEditLabelInputVisible] = useState(false);
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { currentClinic } = useClinicUser();
  const searchInput = useRef<HTMLInputElement | null>(null);
  const { modules } = useModularity();
  const roomIds = useMemo(() => userGroup.rooms.map((r) => r.id), [userGroup.rooms]);
  const appointmentTypeIds = useMemo(() => userGroup.appointmentTypes.map((at) => at.id), [userGroup.appointmentTypes]);
  const employeeIds = useMemo(() => userGroup.clinicEmployees.map((r) => r.id), [userGroup.clinicEmployees]);
  const isAllGroup = useMemo(() => userGroup.id === '_all', [userGroup.id]);
  const isNewGroup = useMemo(
    () => !isAllGroup && !employeeIds.length && !roomIds.length && !appointmentTypeIds.length,
    [isAllGroup, appointmentTypeIds, roomIds, employeeIds],
  );

  const [updateUserAppointmentGrouping, { loading: isUpdatingAppointmentGroup }] =
    useUpdateOneUserAppointmentGroupingMutation({
      refetchQueries: [GetUserAppointmentGroupingsDocument],
    });

  const dateRange = useMemo(
    () => ({
      gte: startOfDay(currentDay).toISOString(),
      lte: endOfDay(currentDay).toISOString(),
    }),
    [currentDay],
  );

  const { appointments, totalAppointments, appointmentsPage, hasNextPage, setAppointmentsPage, isLoadingAppointments } =
    useGetPaginatedAppointments({
      clinicId: currentClinic?.id,
      numberOfAppointmentsPerPage: appointmentsPerPage,
      roomIds,
      dateRange,
      appointmentTypeIds,
      employeeIds,
      isNewGroup,
      petName: searchTerm,
      channelStatusIds,
      shouldFetchCountOnly: false,
    });

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.currentTarget.value);
    },
    [setSearchTerm],
  );

  const updateAppointmentGroupLabel = useCallback(() => {
    if (label) {
      updateUserAppointmentGrouping({
        variables: {
          data: {
            label,
          },
          where: {
            id: userGroup.id,
          },
        },
        onCompleted: () => {
          setIsEditLabelInputVisible(false);
        },
      });
    }
  }, [label, updateUserAppointmentGrouping, userGroup.id]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
      const canGetNextPage = scrollTop + clientHeight + scrollPixelVariance >= scrollHeight;
      if (hasNextPage && canGetNextPage && !isLoadingAppointments) {
        setAppointmentsPage(appointmentsPage + 1);
      }
    },
    [appointmentsPage, hasNextPage, setAppointmentsPage, isLoadingAppointments],
  );

  const groupFilterChanged = useCallback(
    (selectedRooms: string[], selectedTypes: string[], selectedProviders: string[]) => {
      updateUserAppointmentGrouping({
        variables: {
          data: {
            appointmentTypes: {
              set: selectedTypes.map((id) => ({ id })),
            },
            rooms: {
              set: selectedRooms.map((id) => ({ id })),
            },
            clinicEmployees: { set: selectedProviders.map((id) => ({ id })) },
          },
          where: {
            id: userGroup.id,
          },
        },
      });
    },
    [updateUserAppointmentGrouping, userGroup.id],
  );

  const handleToggleSearchInputVisibilty = useCallback(() => {
    setIsSearchInputVisible((prevState) => !prevState);
    setSearchTerm('');

    // Set timeout allows for the input to load into the dom fully before attempting to focus
    setTimeout(() => {
      searchInput.current?.focus();
    }, 200);
  }, [searchInput]);

  const handleLabelChange = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        updateAppointmentGroupLabel();
      }
    },
    [updateAppointmentGroupLabel],
  );

  const noAppointmentsFound = useMemo(() => {
    return !isLoadingAppointments && (!appointments || appointments.length <= 0);
  }, [appointments, isLoadingAppointments]);
  const { isMobile } = useResolutionProvider();

  const isAppointmentStandard = useMemo(() => {
    return modules?.includes(ModuleName.AppointmentsStandard);
  }, [modules]);

  return (
    <VStack
      className="AppointmentGroup__Column"
      bg="background.alpha"
      borderRadius="xl"
      h="100%"
      minW={!isAppointmentStandard && isMobile ? '100%' : '372px'}
      maxW={!isAppointmentStandard && isMobile ? '100%' : '372px'}
      pos="relative"
    >
      <HStack
        className="AppointmentGroup__Header"
        bg="background.default"
        minH="55px"
        p={2}
        pl={4}
        mb={isSearchInputVisible ? '50px' : 0}
        w="100%"
        boxShadow="sm"
        borderBottom="4px"
        borderTopRadius="xl"
        borderTop="1px"
        borderLeft="1px"
        borderRight="1px"
        borderColor="border.default"
        justify="space-between"
        pos="relative"
      >
        {isEditLabelInputVisible && !isAllGroup ? (
          <TextInput
            className="AppointmentGroup__LabelInput"
            value={label}
            size="sm"
            onChange={(e): void => setLabel(e.target.value)}
            autoFocus
            onBlur={updateAppointmentGroupLabel}
            isDisabled={isUpdatingAppointmentGroup}
            onKeyDown={handleLabelChange}
          />
        ) : (
          <HStack className="AppointmentGroup__Label" spacing={0}>
            <HStack spacing={2}>
              <Square px={1} bg="background.lessSubtle" borderRadius="lg">
                <Text size="xs">
                  {totalAppointments ? `${totalAppointments > 999 ? '999+' : totalAppointments}` : '0'}
                </Text>
              </Square>
              <Text isTruncated size="lg" fontWeight="bold">
                {userGroup.label}{' '}
              </Text>
            </HStack>
            {!isAllGroup && (
              <Button
                variant="ghostNeutral"
                size="sm"
                iconName="pen"
                onClick={(): void => setIsEditLabelInputVisible(true)}
              />
            )}
          </HStack>
        )}

        <HStack spacing={0} className="AppointmentGroup__Buttons">
          <Button
            variant="ghostNeutral"
            size="sm"
            iconName="magnifyingGlass"
            onClick={handleToggleSearchInputVisibilty}
            isActive={isSearchInputVisible}
          />
          {!isAllGroup && (
            <>
              <FilterAppointmentsMenu
                selectedProviders={employeeIds}
                selectedRooms={roomIds}
                selectedTypes={appointmentTypeIds}
                onChange={groupFilterChanged}
              />
              <Button
                variant="ghostNeutral"
                size="sm"
                iconName="trashcan"
                onClick={partial(onDeleteUserAppointmentGroup, userGroup.id)}
              />
            </>
          )}
        </HStack>

        {isSearchInputVisible && (
          <Box pos="absolute" bottom="-60px" left="0" w="95%">
            <TextInput size="sm" my={3} placeholder="Search pet name..." ref={searchInput} onChange={handleSearch} />
          </Box>
        )}
      </HStack>
      <VStack
        className="AppointmentGroup__ScrollContainer"
        overflowY="auto"
        maxH="100%"
        w="100%"
        px={3}
        pb={4}
        spacing={3}
        onScroll={handleScroll}
        pos="relative"
      >
        {appointments?.map((appointment, index) => {
          const previousAppt = appointments[index - 1];
          return (
            <AppointmentCard
              appointment={appointment}
              key={`${userGroup.id}_${appointment.id}`}
              onAppointmentClicked={onAppointmentClicked}
              showTimeGroup={previousAppt ? appointment.startAt !== previousAppt.startAt : true}
            />
          );
        })}
        {isLoadingAppointments && (
          <Box className="AppointmentGroup__ScrollContainer" mt={8}>
            <Spinner size="md" />
          </Box>
        )}
        {noAppointmentsFound && (
          <Text size="sm" fontStyle="italic" mt={4} variant="subtle">
            No appointments found
          </Text>
        )}
      </VStack>
    </VStack>
  );
}
